import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RedeemIcon from '@mui/icons-material/Redeem';
import { LoadingButton } from '@mui/lab';
import Alert from '@mui/material/Alert';
import { Box, FormControlLabel, Grid, InputAdornment, Radio, RadioGroup, Stack, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { AsyncDialog, AsyncDialogRef } from '../../../../components/AsyncDialog/AsyncDialog';
import {
  ConfirmationDialog,
  ConfirmationDialogData,
} from '../../../../components/ConfirmationDialog/ConfirmationDialog';
import { FakeButtonLink } from '../../../../components/FakeLink';
import { SectionCard } from '../../../../components/Section';
import { treasuryRedemptionFormFields as F } from '../../../../forms/fields/formFields/treasuryRedemption';
import { treasuryRedemptionSchema } from '../../../../forms/validationSchema/formSchema/treasuryRedemption';
import { usePermissions } from '../../../../hooks';
import { useCreateTreasuryRedemptionMutation } from '../../../../redux/features/treasury/treasury.apiSlice';
import { locations } from '../../../../routes/locations';
import { SubscribedPortfolio } from '../../../../types/subscribedPortfolio';
import { TreasuryRedemptionType } from '../../../../types/treasury';
import * as permissions from '../../../../utils/permissions';
import { getTreasuryErrorMessage } from '../../../../utils/treasury';
import { TreasuryPortfolioSelect } from './TreasuryPortfolioSelect';
import { TreasuryRedemptionRequestsList } from './TreasuryRedemptionRequestsList';

type TreasuryRedemptionTabProps = {
  companyId: number;
  /** The treasury employee's user — the portfolio's requests are listed under them. */
  ownerUserId: number | null;
  portfolios: SubscribedPortfolio[];
  isPortfoliosLoading: boolean;
};

type RedemptionFormValues = {
  [F.portfolioId]: number | '';
  [F.type]: TreasuryRedemptionType;
  [F.amount]: string;
  [F.reason]: string;
};

/**
 * Redemptions — the company takes money out of a treasury portfolio. PARTIAL or FULL, approved at creation (no
 * company approval, no vesting), 100% company, credited to the treasury bank. thriftplan answers 409 while another
 * redemption of the same portfolio is in flight. Below the form: the portfolio's own redemption requests from
 * madkhol-portfolio (thriftplan has no treasury redemption list).
 */
export const TreasuryRedemptionTab = ({
  companyId,
  ownerUserId,
  portfolios,
  isPortfoliosLoading,
}: TreasuryRedemptionTabProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const canRedeem = usePermissions({ items: [permissions.Treasury.createRedemption] });
  const confirmRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const [createRedemption, { isLoading: isSubmitting }] = useCreateTreasuryRedemptionMutation();
  const [lastRequestId, setLastRequestId] = useState<number | null>(null);
  // Bumped after a redemption so the portfolio's request list mounts again and re-reads.
  const [listVersion, setListVersion] = useState(0);
  // The treasury group has one product, so one portfolio (the list comes newest first).
  const [listedPortfolio] = portfolios;

  const {
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    touched,
    errors,
    isValid,
    submitForm,
    resetForm,
    validateForm,
  } = useFormik<RedemptionFormValues>({
    initialValues: { [F.portfolioId]: '', [F.type]: 'PARTIAL', [F.amount]: '', [F.reason]: '' },
    validationSchema: treasuryRedemptionSchema,
    validateOnMount: true,
    onSubmit: async (formValues) => {
      const portfolioId = formValues[F.portfolioId];

      if (portfolioId === '') return;

      const type = formValues[F.type];
      const reason = formValues[F.reason].trim();

      try {
        const response = await createRedemption({
          companyId,
          payload: {
            externalSubscribedPortfolioId: portfolioId,
            type,
            ...(type === 'PARTIAL' ? { amount: Number(formValues[F.amount]) } : {}),
            ...(reason ? { reason } : {}),
          },
        }).unwrap();
        setLastRequestId(response?.id ?? null);
        setListVersion((version) => version + 1);

        // Keep the portfolio and type; clear the rest — and validate again (resetForm alone clears the errors).
        const nextValues = { ...formValues, [F.amount]: '', [F.reason]: '' };
        resetForm({ values: nextValues });
        validateForm(nextValues);
        enqueueSnackbar('Redemption started', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(getTreasuryErrorMessage(error, 'Failed to start the redemption'), { variant: 'error' });
      }
    },
  });

  const portfolioId = values[F.portfolioId];
  const type = values[F.type];
  const amountError = touched[F.amount] && errors[F.amount];

  const confirm = () =>
    confirmRef.current?.show({
      title: type === 'FULL' ? 'Redeem Everything' : 'Redeem',
      description:
        type === 'FULL'
          ? `Sell the whole portfolio #${portfolioId}? It is approved at once and can't be cancelled.`
          : `Sell SAR ${Number(
              values[F.amount],
            )} from portfolio #${portfolioId}? It is approved at once and can't be cancelled.`,
      acceptButtonText: 'Redeem',
      rejectButtonText: 'Cancel',
      confirmButtonColor: 'warning',
      onConfirm: submitForm,
    });

  return (
    <Stack spacing={2}>
      {lastRequestId !== null && (
        <Alert severity="success" onClose={() => setLastRequestId(null)}>
          Redeem request #{lastRequestId} created and approved. Follow it on the portfolio.
        </Alert>
      )}

      <SectionCard
        title="New Redemption"
        description="Approved at creation. The money goes to the company treasury bank."
        action={
          portfolioId !== '' && (
            <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.subscribedPortfolio(portfolioId)}>
              Portfolio #{portfolioId}
            </FakeButtonLink>
          )
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TreasuryPortfolioSelect
              portfolios={portfolios}
              value={portfolioId}
              onChange={(id) => setFieldValue(F.portfolioId, id)}
              isLoading={isPortfoliosLoading}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RadioGroup row name={F.type} value={type} onChange={handleChange}>
              <FormControlLabel value="PARTIAL" control={<Radio />} label="Partial" />
              <FormControlLabel value="FULL" control={<Radio />} label="Full (everything)" />
            </RadioGroup>
          </Grid>
          {type === 'PARTIAL' && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                id={F.amount}
                name={F.amount}
                label="Amount"
                type="number"
                value={values[F.amount]}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(amountError)}
                helperText={
                  amountError || 'Within the market value and the portfolio minimum — thriftplan checks both.'
                }
                InputProps={{ startAdornment: <InputAdornment position="start">SAR</InputAdornment> }}
              />
            </Grid>
          )}
          <Grid item xs={12} md={type === 'PARTIAL' ? 6 : 12}>
            <TextField
              fullWidth
              size="small"
              id={F.reason}
              name={F.reason}
              label="Reason (optional)"
              value={values[F.reason]}
              onChange={handleChange}
              inputProps={{ maxLength: 1000 }}
            />
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <LoadingButton
            variant="contained"
            color="warning"
            startIcon={<RedeemIcon />}
            loading={isSubmitting}
            disabled={!canRedeem || !isValid || isSubmitting}
            onClick={confirm}
          >
            Redeem
          </LoadingButton>
        </Box>
      </SectionCard>

      {ownerUserId && listedPortfolio && (
        <TreasuryRedemptionRequestsList
          ownerUserId={ownerUserId}
          subscribedPortfolioId={listedPortfolio.id}
          reloadKey={listVersion}
        />
      )}
      <AsyncDialog ref={confirmRef} dialog={ConfirmationDialog} />
    </Stack>
  );
};
